"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState } from "react";
import Link from "next/link";
import { SectionHeader, CTAButton, StatsTable, EntityDisplay, type StatsTableColumn, type StatsTableRow } from "fansunited-sports-ui";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { PillToggle } from "@/components/ui/controls/PillToggle";
import { chunkGroups, getChunkTabLabel } from "./utils";
import type { FUSportsStandingsEntryWithNativeTeamId, StandingsGroup } from "@/utilities/data/types";
const DEFAULT_GROUPS_PER_TAB = 2;
const MAX_COMPACT_ROWS = 20;

/**
 * Short label for a group ("A", "B", …) used in tab chips and fallback
 * headers. Strips a leading "Group " prefix from the API-provided name so
 * `"Group A"` → `"A"`; falls back to A/B/C by index when the name is missing.
 */
const groupShortName = (group: StandingsGroup, index: number): string => {
  const raw = group.name?.replace(/^group\s+/i, "").trim();
  return raw || String.fromCharCode(65 + index);
};
const tableStyles = {
  container: "bg-card border border-border rounded-lg",
  borderRadius: "md" as const,
  shadow: "none" as const,
  typography: {
    cell: "text-sm font-medium text-card-foreground",
    header: "text-xs uppercase font-bold text-muted-foreground"
  }
};
interface InternationalStandingsCompactProps {
  /**
   * Pre-bucketed groups (from the FansUnited hydrated competition endpoint or a
   * consumer-side bucketizer). Each carries the API-provided `name` and its
   * ordered `entries`.
   */
  groups: StandingsGroup[];
  showHeader?: boolean;
  /** Override the standings URL (e.g. for competition-specific links). Defaults to nav.standings(). */
  standingsUrl?: string;
  /** Number of groups per tab. Defaults to 2. */
  groupsPerTab?: number;
}

/**
 * International competition compact standings.
 * Shows groups chunked into tabs (default 2 per tab), each stacked vertically.
 */
export const InternationalStandingsCompact: React.FC<InternationalStandingsCompactProps> = t0 => {
  const $ = _c(77);
  const {
    groups,
    showHeader: t1,
    standingsUrl,
    groupsPerTab: t2
  } = t0;
  const showHeader = t1 === undefined ? true : t1;
  const groupsPerTab = t2 === undefined ? DEFAULT_GROUPS_PER_TAB : t2;
  const labels = useLabels();
  const nav = useNavigation();
  let t3;
  if ($[0] !== nav || $[1] !== standingsUrl) {
    t3 = standingsUrl ?? nav.standings();
    $[0] = nav;
    $[1] = standingsUrl;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const fullTableUrl = t3;
  let t4;
  if ($[3] !== groups) {
    t4 = groups.filter(_temp);
    $[3] = groups;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const nonEmptyGroups = t4;
  let t5;
  if ($[5] !== nonEmptyGroups) {
    t5 = nonEmptyGroups.flatMap(_temp2);
    $[5] = nonEmptyGroups;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const flatEntries = t5;
  let t6;
  if ($[7] !== groupsPerTab || $[8] !== nonEmptyGroups) {
    t6 = nonEmptyGroups.length > 0 ? chunkGroups(nonEmptyGroups, groupsPerTab) : [];
    $[7] = groupsPerTab;
    $[8] = nonEmptyGroups;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const chunks = t6;
  let t7;
  if ($[10] !== chunks || $[11] !== groupsPerTab) {
    let t8;
    if ($[13] !== groupsPerTab) {
      t8 = (chunk, chunkIndex) => ({
        id: String(chunkIndex),
        label: getChunkTabLabel(chunk, g_1 => groupShortName(g_1, chunkIndex * groupsPerTab))
      });
      $[13] = groupsPerTab;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    t7 = chunks.map(t8);
    $[10] = chunks;
    $[11] = groupsPerTab;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const tabOptions = t7;
  const [activeTabId, setActiveTabId] = useState("0");
  const index = parseInt(activeTabId, 10);
  let t8;
  if ($[15] !== chunks || $[16] !== index) {
    t8 = chunks[index] ?? [];
    $[15] = chunks;
    $[16] = index;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const activeChunk = t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      key: "rank",
      header: "#",
      align: "left",
      width: "xs"
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== nav) {
    t10 = v => {
      const t = v as {
        name: string;
        logo?: string;
        fansUnitedTeamId?: string | null;
      };
      const teamUrl = t.fansUnitedTeamId ? nav.team(t.fansUnitedTeamId) : "#";
      return <Link prefetch={false} href={teamUrl}><EntityDisplay name={t.name} image={<TeamLogo src={t.logo} size="sm" />} /></Link>;
    };
    $[19] = nav;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== labels.standings.table.team || $[22] !== t10) {
    t11 = {
      key: "team",
      header: labels.standings.table.team,
      align: "left",
      width: "flex",
      render: t10
    };
    $[21] = labels.standings.table.team;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== labels.standings.table.played) {
    t12 = {
      key: "played",
      header: labels.standings.table.played,
      align: "center",
      width: "sm"
    };
    $[24] = labels.standings.table.played;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== labels.standings.table.points) {
    t13 = {
      key: "points",
      header: labels.standings.table.points,
      align: "center",
      width: "sm",
      className: "font-bold"
    };
    $[26] = labels.standings.table.points;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t11 || $[29] !== t12 || $[30] !== t13) {
    t14 = [t9, t11, t12, t13];
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const columns = t14;
  if (chunks.length === 0) {
    const isTruncated = flatEntries.length > MAX_COMPACT_ROWS;
    let t15;
    if ($[32] !== flatEntries || $[33] !== isTruncated) {
      t15 = isTruncated ? flatEntries.slice(0, MAX_COMPACT_ROWS) : flatEntries;
      $[32] = flatEntries;
      $[33] = isTruncated;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    let t16;
    if ($[35] !== t15) {
      t16 = mapEntriesToRows(t15);
      $[35] = t15;
      $[36] = t16;
    } else {
      t16 = $[36];
    }
    const rows = t16;
    if (rows.length === 0) {
      return null;
    }
    let t17;
    if ($[37] !== fullTableUrl || $[38] !== labels || $[39] !== nav || $[40] !== showHeader) {
      t17 = showHeader && <CompactHeader labels={labels} nav={nav} standingsUrl={fullTableUrl} />;
      $[37] = fullTableUrl;
      $[38] = labels;
      $[39] = nav;
      $[40] = showHeader;
      $[41] = t17;
    } else {
      t17 = $[41];
    }
    let t18;
    if ($[42] !== columns || $[43] !== rows) {
      t18 = <StatsTable columns={columns} rows={rows} styles={tableStyles} />;
      $[42] = columns;
      $[43] = rows;
      $[44] = t18;
    } else {
      t18 = $[44];
    }
    let t19;
    if ($[45] !== fullTableUrl || $[46] !== labels.homePage.fullTable) {
      t19 = <div className="mt-3 text-right"><Link prefetch={false} href={fullTableUrl} className="text-sm font-medium link-themed hover:underline">{labels.homePage.fullTable}</Link></div>;
      $[45] = fullTableUrl;
      $[46] = labels.homePage.fullTable;
      $[47] = t19;
    } else {
      t19 = $[47];
    }
    let t20;
    if ($[48] !== t17 || $[49] !== t18 || $[50] !== t19) {
      t20 = <section>{t17}{t18}{t19}</section>;
      $[48] = t17;
      $[49] = t18;
      $[50] = t19;
      $[51] = t20;
    } else {
      t20 = $[51];
    }
    return t20;
  }
  let t15;
  if ($[52] !== fullTableUrl || $[53] !== labels || $[54] !== nav || $[55] !== showHeader) {
    t15 = showHeader && <CompactHeader labels={labels} nav={nav} standingsUrl={fullTableUrl} />;
    $[52] = fullTableUrl;
    $[53] = labels;
    $[54] = nav;
    $[55] = showHeader;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  let t16;
  if ($[57] !== activeTabId || $[58] !== tabOptions) {
    t16 = tabOptions.length > 1 && <PillToggle options={tabOptions} activeId={activeTabId} onChange={setActiveTabId} size="sm" className="mb-3" />;
    $[57] = activeTabId;
    $[58] = tabOptions;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== activeChunk || $[61] !== columns || $[62] !== labels.standings.table.group) {
    let t18;
    if ($[64] !== columns || $[65] !== labels.standings.table.group) {
      t18 = (group, groupIndex) => {
        const displayName = groupShortName(group, groupIndex);
        return <div key={group.id ?? displayName}><h4 className="text-xs uppercase font-bold text-muted-foreground mb-2">{group.name ?? `${labels.standings.table.group} ${displayName}`}</h4><StatsTable columns={columns} rows={mapEntriesToRows(group.entries)} styles={tableStyles} /></div>;
      };
      $[64] = columns;
      $[65] = labels.standings.table.group;
      $[66] = t18;
    } else {
      t18 = $[66];
    }
    t17 = activeChunk.map(t18);
    $[60] = activeChunk;
    $[61] = columns;
    $[62] = labels.standings.table.group;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[67] !== t17) {
    t18 = <div className="space-y-4">{t17}</div>;
    $[67] = t17;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  let t19;
  if ($[69] !== fullTableUrl || $[70] !== labels.homePage.fullTable) {
    t19 = <div className="mt-3 text-right"><Link prefetch={false} href={fullTableUrl} className="text-sm font-medium text-accent hover:text-accent/80">{labels.homePage.fullTable}</Link></div>;
    $[69] = fullTableUrl;
    $[70] = labels.homePage.fullTable;
    $[71] = t19;
  } else {
    t19 = $[71];
  }
  let t20;
  if ($[72] !== t15 || $[73] !== t16 || $[74] !== t18 || $[75] !== t19) {
    t20 = <section>{t15}{t16}{t18}{t19}</section>;
    $[72] = t15;
    $[73] = t16;
    $[74] = t18;
    $[75] = t19;
    $[76] = t20;
  } else {
    t20 = $[76];
  }
  return t20;
};

/** Section header with "Full Table" link */
function CompactHeader(t0) {
  const $ = _c(14);
  const {
    labels,
    nav,
    standingsUrl
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      typography: {
        name: "text-sm uppercase font-bold text-foreground"
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== labels.homePage.standings) {
    t2 = {
      name: labels.homePage.standings,
      styles: t1
    };
    $[1] = labels.homePage.standings;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== nav || $[4] !== standingsUrl) {
    t3 = standingsUrl ?? nav.standings();
    $[3] = nav;
    $[4] = standingsUrl;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      container: "text-sm font-medium text-accent hover:text-accent/80 border-none p-0"
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== labels.homePage.fullTable || $[8] !== t3) {
    t5 = <CTAButton text={labels.homePage.fullTable} href={t3} variant="custom" styles={t4} />;
    $[7] = labels.homePage.fullTable;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      container: "mb-4"
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t2 || $[12] !== t5) {
    t7 = <SectionHeader entity={t2} primaryCta={t5} styles={t6} />;
    $[11] = t2;
    $[12] = t5;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}

/** Map flat standings entries to compact table rows */
function mapEntriesToRows(entries: FUSportsStandingsEntryWithNativeTeamId[]): StatsTableRow[] {
  return entries.map((entry, index) => ({
    id: entry.competitor.id,
    rank: entry.rank ?? index + 1,
    team: {
      name: entry.competitor.name,
      logo: entry.competitor.logo,
      fansUnitedTeamId: entry.competitor.fansUnitedTeamId
    },
    played: entry.stats?.played ?? 0,
    points: entry.stats?.points ?? 0
  }));
}
function _temp(g) {
  return g.entries.length > 0;
}
function _temp2(g_0) {
  return g_0.entries;
}